<template>
  <dialog-comp
    v-model="modalOpen"
    :action-button-label="actionButtonLabel"
    max-width="600px"
    :modal-title="modalTitle"
    suppress-close-on-action-button-click
    width="100%"
    @cancel-clicked="onReset"
    @confirm-clicked="onSubmit"
  >
    <template #content>
      <div class="row q-col-gutter-md q-px-md q-pt-md">
        <div class="col-12">
          <q-input
            v-if="email === ''"
            v-model="dialogEmail"
            dense
            label="Email *"
            lazy-rules
            :rules="[Rule.REQUIRED, Rule.UNUSED_EMAIL]"
            ref="emailInput"
          />
          <q-field
            v-else
            dense
            hint=""
            label="Email"
            stack-label
            ref="emailInput"
          >
            <template #control>
              <div class="self-center full-width no-outline">
                {{ dialogEmail }}
              </div>
            </template>
          </q-field>
        </div>
        <div class="col-12">
          <q-input
            v-model="inputs.firstName"
            dense
            label="First Name *"
            :rules="[Rule.REQUIRED]"
            ref="firstNameInput"
          />
        </div>
        <div class="col-12">
          <q-input
            v-model="inputs.lastName"
            dense
            label="Last Name *"
            :rules="[Rule.REQUIRED]"
            ref="lastNameInput"
          />
        </div>
        <div class="col-12">
          <q-input
            v-model="inputs.phoneNumber"
            dense
            label="Phone Number"
            mask="(###) ### - ####"
            unmasked-value
            ref="phoneNumberInput"
          />
        </div>
      </div>
    </template>
  </dialog-comp>
</template>

<script>
import _ from "lodash";
import DialogComp from "@/components/common/DialogComp";
import { ref, watch } from "vue";
import { useSyncModelValue } from "@/utils/helpers/useSyncModelValue";
import { useValidateInputs } from "@/utils/helpers/useValidateInputs";
import Rule from "@/utils/Rule";

export default {
  name: "AdminDialog",
  components: { DialogComp },
  props: {
    adminAccount: {
      type: Object,
      default: () => {},
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    email: {
      type: String,
      default: null,
    },
  },
  emits: [
    "update:modelValue",
    "update:email",
    "cancelClicked",
    "updateClicked",
    "update-clicked",
  ],

  setup: function (props, { emit }) {
    const modalOpen = ref(props.modelValue);

    const inputs = ref({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      type: "ADMIN",
    });

    watch(
      () => props.modelValue,
      () => {
        if (
          props.adminAccount.email !== "" &&
          props.adminAccount.firstName !== "" &&
          props.adminAccount.lastName !== ""
        ) {
          inputs.value = _.cloneDeep(props.adminAccount);
        } else {
          inputs.value = {
            email: "",
            firstName: "",
            lastName: "",
            phoneNumber: "",
            type: "ADMIN",
          };
        }
      }
    );

    useSyncModelValue(
      props,
      "modelValue",
      emit,
      "update:modelValue",
      modalOpen
    );

    const dialogEmail = ref();
    useSyncModelValue(props, "email", emit, "update:email", dialogEmail);

    function cancelClicked() {
      inputs.value = props.comment;
      emit("cancelClicked");
    }

    function updateClicked() {
      emit("update-clicked", {
        email: dialogEmail.value.trim(),
        account: inputs.value,
      });
    }

    const emailInput = ref();
    const firstNameInput = ref();
    const lastNameInput = ref();
    const phoneNumberInput = ref();
    const inputRefs = [
      emailInput,
      firstNameInput,
      lastNameInput,
      phoneNumberInput,
    ];

    const { validateAndRunFunctionIfNoError, resetValidation } =
      useValidateInputs(inputRefs);

    function onSubmit() {
      validateAndRunFunctionIfNoError(updateClicked);
    }

    function onReset() {
      resetValidation();
      cancelClicked();
    }

    function clearInputs() {
      inputs.value = {
        firstName: "",
        lastName: "",
        phoneNumber: "",
      };
    }

    const modalTitle = "Enter new admin account information";
    const actionButtonLabel = "Submit";

    watch(
      () => props.modelValue,
      (newVal) => {
        dialogEmail.value = props.email;
        if (!newVal) {
          clearInputs();
        }
      }
    );

    return {
      modalOpen,
      cancelClicked,
      updateClicked,
      inputs,
      dialogEmail,
      onSubmit,
      onReset,
      modalTitle,
      actionButtonLabel,
      emailInput,
      firstNameInput,
      lastNameInput,
      phoneNumberInput,
      Rule,
    };
  },
};
</script>

<style scoped></style>
